'use client';

import type { ReactNode } from 'react';
import { useCallback, useEffect, useState } from 'react';
import LottieLoader from './LottieLoader';

let hasCompletedBootAnimationForPageLoad = false;

const getHasCompletedBootAnimation = () => hasCompletedBootAnimationForPageLoad;

const setHasCompletedBootAnimation = () => {
  hasCompletedBootAnimationForPageLoad = true;
};

// workspace-redirect sets this short-lived cookie on the login -> workspace
// hop; that redirect must not show any loading indicator. Consumed exactly
// once (then deleted), so hard refreshes still show the "P" Lottie.
const SKIP_BOOT_LOADER_COOKIE = 'skipBootLoader';

const consumeSkipBootLoader = (): boolean => {
  if (typeof document === 'undefined') {
    return false;
  }

  const hasFlag = document.cookie
    .split('; ')
    .some((cookie) => cookie === `${SKIP_BOOT_LOADER_COOKIE}=1`);

  if (hasFlag) {
    document.cookie = `${SKIP_BOOT_LOADER_COOKIE}=; path=/; max-age=0`;
  }

  return hasFlag;
};

interface AppBootLoaderProps {
  children: ReactNode;
  onComplete?: () => void;
  delayMs?: number;
  isPending?: boolean;
}

export default function AppBootLoader({
  children,
  onComplete,
  delayMs = 180,
  isPending = false,
}: AppBootLoaderProps) {
  const [hasCompletedAnimation, setHasCompletedAnimation] = useState(getHasCompletedBootAnimation);
  const [shouldShowAnimation, setShouldShowAnimation] = useState(false);

  // Login -> workspace handoff: skip the boot animation once, silently.
  useEffect(() => {
    if (consumeSkipBootLoader()) {
      setHasCompletedBootAnimation();
      setHasCompletedAnimation(true);
      onComplete?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!getHasCompletedBootAnimation()) {
      return;
    }

    setHasCompletedAnimation(true);
    onComplete?.();
  }, [onComplete]);

  useEffect(() => {
    if (hasCompletedAnimation || getHasCompletedBootAnimation()) {
      return;
    }

    if (!isPending) {
      setHasCompletedBootAnimation();
      setHasCompletedAnimation(true);
      onComplete?.();
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setShouldShowAnimation(true);
    }, delayMs);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [delayMs, hasCompletedAnimation, isPending, onComplete]);

  const handleComplete = useCallback(() => {
    if (!getHasCompletedBootAnimation()) {
      setHasCompletedBootAnimation();
    }

    setHasCompletedAnimation(true);
    onComplete?.();
  }, [onComplete]);

  if (!hasCompletedAnimation) {
    return (
      <>
        {children}
        {shouldShowAnimation ? <LottieLoader loop={false} onComplete={handleComplete} /> : null}
      </>
    );
  }

  return <>{children}</>;
}
